/**
 * Validation Module
 * Validates source table data against target table structure
 */

export interface ValidationResult {
  columnValidation: Map<string, boolean>; // column header -> is matched
  rowValidation: Map<number, boolean>; // row index -> is matched
  grouperColumns: string[]; // Column names that define row keys (e.g., ["Group"])
  matchedRows: number;
  unmatchedRows: number;
}

/**
 * Validate source table data against target table data
 * @param sourceData - Source table as 2D array
 * @param targetData - Target table as 2D array
 * @param grouperColumnLabels - Column labels that serve as row keys (e.g., ["Group"])
 * @returns Validation result with column and row validation
 */
export function validateSourceAgainstTarget(
  sourceData: any[][],
  targetData: any[][],
  grouperColumnLabels: string[]
): ValidationResult {
  const result: ValidationResult = {
    columnValidation: new Map(),
    rowValidation: new Map(),
    grouperColumns: grouperColumnLabels,
    matchedRows: 0,
    unmatchedRows: 0
  };
  
  if (!sourceData || sourceData.length === 0 || !targetData || targetData.length === 0) {
    return result;
  }
  
  const sourceHeaders = sourceData[0];
  const targetHeaders = targetData[0];
  const sourceRows = sourceData.slice(1);
  const targetRows = targetData.slice(1);
  
  // Validate columns (case-insensitive match)
  sourceHeaders.forEach((sourceHeader: string) => {
    const normalizedSourceHeader = sourceHeader?.toString().toLowerCase().trim();
    const isMatched = targetHeaders.some((targetHeader: any) => 
      targetHeader?.toString().toLowerCase().trim() === normalizedSourceHeader
    );
    result.columnValidation.set(sourceHeader, isMatched);
  });
  
  // Find grouper column indices
  const sourceGrouperIndices: number[] = [];
  const targetGrouperIndices: number[] = [];
  
  // Handle case where first label is source and second is target (for reference tables with different labels)
  if (grouperColumnLabels.length === 2 && grouperColumnLabels[0] !== grouperColumnLabels[1]) {
    // Separate source and target labels
    const sourceLabel = grouperColumnLabels[0];
    const targetLabel = grouperColumnLabels[1];
    
    const normalizedSourceLabel = String(sourceLabel).toLowerCase().trim();
    const normalizedTargetLabel = String(targetLabel).toLowerCase().trim();
    
    const sourceIndex = sourceHeaders.findIndex((h: any) => {
      if (h == null) return false;
      try {
        return String(h).toLowerCase().trim() === normalizedSourceLabel;
      } catch {
        return false;
      }
    });
    if (sourceIndex !== -1) {
      sourceGrouperIndices.push(sourceIndex);
    }
    
    const targetIndex = targetHeaders.findIndex((h: any) => {
      if (h == null) return false;
      try {
        return String(h).toLowerCase().trim() === normalizedTargetLabel;
      } catch {
        return false;
      }
    });
    if (targetIndex !== -1) {
      targetGrouperIndices.push(targetIndex);
    }
  } else {
    // Standard case: same label in both source and target
    grouperColumnLabels.forEach(grouperLabel => {
      if (!grouperLabel) return;
      
      const normalizedLabel = String(grouperLabel).toLowerCase().trim();
      
      const sourceIndex = sourceHeaders.findIndex((h: any) => {
        if (h == null) return false;
        try {
          return String(h).toLowerCase().trim() === normalizedLabel;
        } catch {
          return false;
        }
      });
      if (sourceIndex !== -1) {
        sourceGrouperIndices.push(sourceIndex);
      }
      
      const targetIndex = targetHeaders.findIndex((h: any) => {
        if (h == null) return false;
        try {
          return String(h).toLowerCase().trim() === normalizedLabel;
        } catch {
          return false;
        }
      });
      if (targetIndex !== -1) {
        targetGrouperIndices.push(targetIndex);
      }
    });
  }
  
  // Validate rows based on grouper column values
  if (sourceGrouperIndices.length > 0 && targetGrouperIndices.length > 0) {
    // Build target row key map
    const targetRowKeys = new Set<string>();
    targetRows.forEach((targetRow: any[]) => {
      const rowKey = buildRowKey(targetRow, targetGrouperIndices);
      targetRowKeys.add(rowKey);
    });
    
    // Validate source rows
    sourceRows.forEach((sourceRow: any[], rowIndex: number) => {
      const rowKey = buildRowKey(sourceRow, sourceGrouperIndices);
      const isMatched = targetRowKeys.has(rowKey);
      result.rowValidation.set(rowIndex + 1, isMatched); // +1 because row 0 is headers
      
      if (isMatched) {
        result.matchedRows++;
      } else {
        result.unmatchedRows++;
      }
    });
  } else {
    // No grouper columns found - mark all rows as unmatched
    sourceRows.forEach((_: any, rowIndex: number) => {
      result.rowValidation.set(rowIndex + 1, false);
      result.unmatchedRows++;
    });
  }
  
  return result;
}

/**
 * Build a row key from grouper column values
 */
function buildRowKey(row: any[], grouperIndices: number[]): string {
  return grouperIndices
    .map(index => row[index]?.toString().toLowerCase().trim() || '')
    .join('|');
}

/**
 * Get validation summary message
 */
export function getValidationSummary(validation: ValidationResult): string {
  const totalColumns = validation.columnValidation.size;
  const matchedColumns = Array.from(validation.columnValidation.values()).filter(v => v).length;
  
  const columnSummary = `Columns: ${matchedColumns}/${totalColumns} matched`;
  const rowSummary = `Rows: ${validation.matchedRows} matched, ${validation.unmatchedRows} unmatched`;
  
  return `${columnSummary}\n${rowSummary}`;
}

